<script setup lang="ts">
/**
 * StatusTimeline — 4-step mini status track on submission cards.
 * Mirrors .status-track in resident-dashboard.html exactly.
 * Steps: Submitted → Acknowledged → In progress → Resolved
 *
 * Progress indicator width is derived from currentStep (0-based):
 *   0 = submitted, 1 = acknowledged, 2 = in_progress, 3 = resolved
 *
 * Severity color map: uses green for resolved/done, navy for active step.
 * All times use font-mono tabular-nums per CLAUDE.md.
 */
interface TimelineStep {
  label: string;
  time?: string | null;
  done: boolean;
  active: boolean;
}

const props = withDefaults(defineProps<{
  status: string;
  submittedAt?: string | null;
  acknowledgedAt?: string | null;
  inProgressAt?: string | null;
  resolvedAt?: string | null;
}>(), {
  submittedAt: null,
  acknowledgedAt: null,
  inProgressAt: null,
  resolvedAt: null,
});

import { computed } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

function fmt(v: string | null | undefined): string {
  if (!v) return '—';
  return dayjs(v).format('h:mm A');
}

const statusLower = computed(() => (props.status ?? '').toLowerCase().replace(/[^a-z_]/g, ''));

// Determine which steps are done/active based on status string.
const steps = computed<TimelineStep[]>(() => {
  const s = statusLower.value;
  const isResolved = s === 'resolved' || s === '3';
  const isInProgress = s === 'inprogress' || s === 'in_progress' || s === '2';
  const isAcknowledged = s === 'acknowledged' || s === '1';
  void (s === 'new' || s === '0' || s === 'submitted'); // default branch

  if (isResolved) {
    return [
      { label: 'Submitted', time: fmt(props.submittedAt), done: true, active: false },
      { label: 'Acknowledged', time: fmt(props.acknowledgedAt), done: true, active: false },
      { label: 'In progress', time: fmt(props.inProgressAt), done: true, active: false },
      { label: 'Resolved', time: fmt(props.resolvedAt), done: true, active: false },
    ];
  }
  if (isInProgress) {
    return [
      { label: 'Submitted', time: fmt(props.submittedAt), done: true, active: false },
      { label: 'Acknowledged', time: fmt(props.acknowledgedAt), done: true, active: false },
      { label: 'In progress', time: fmt(props.inProgressAt), done: false, active: true },
      { label: 'Resolved', time: '—', done: false, active: false },
    ];
  }
  if (isAcknowledged) {
    return [
      { label: 'Submitted', time: fmt(props.submittedAt), done: true, active: false },
      { label: 'Acknowledged', time: fmt(props.acknowledgedAt), done: false, active: true },
      { label: 'In progress', time: '—', done: false, active: false },
      { label: 'Resolved', time: '—', done: false, active: false },
    ];
  }
  // default: new / submitted
  return [
    { label: 'Submitted', time: fmt(props.submittedAt), done: false, active: true },
    { label: 'Acknowledged', time: '—', done: false, active: false },
    { label: 'In progress', time: '—', done: false, active: false },
    { label: 'Resolved', time: '—', done: false, active: false },
  ];
});

// CSS class for the green track width (p0-p3 based on how many done steps there are)
const trackClass = computed(() => {
  const doneCount = steps.value.filter((s) => s.done).length;
  return `p${doneCount}`;
});
</script>

<template>
  <div
    class="status-track"
    :class="trackClass"
    role="list"
    aria-label="Issue status timeline"
    data-testid="status-timeline"
  >
    <div
      v-for="step in steps"
      :key="step.label"
      class="status-step"
      :class="{ done: step.done, active: step.active }"
      role="listitem"
    >
      <div class="st-dot" />
      <div class="st-label">{{ step.label }}</div>
      <div class="st-time font-mono tabular-nums">{{ step.time }}</div>
    </div>
  </div>
</template>

<style scoped>
.status-track {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  margin-top: 14px;
  position: relative;
}
.status-step {
  text-align: center;
  position: relative;
}
.st-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--border);
  margin: 0 auto 6px;
  position: relative;
  z-index: 1;
  border: 2px solid var(--surface);
}
.status-step.done .st-dot { background: var(--green-600); }
.status-step.active .st-dot {
  background: var(--navy-700);
  box-shadow: 0 0 0 4px color-mix(in oklch, var(--navy-700), transparent 80%);
}
.st-label {
  font-size: 11.5px;
  color: var(--muted);
  font-weight: 500;
}
.status-step.done .st-label,
.status-step.active .st-label { color: var(--fg); font-weight: 600; }
.st-time { font-size: 10.5px; color: var(--muted-2); margin-top: 2px; }

/* Track line */
.status-track::before {
  content: '';
  position: absolute;
  top: 5px;
  left: 12.5%;
  right: 12.5%;
  height: 2px;
  background: var(--border);
  z-index: 0;
}
.status-track::after {
  content: '';
  position: absolute;
  top: 5px;
  left: 12.5%;
  height: 2px;
  background: var(--green-600);
  z-index: 0;
}
.status-track.p0::after { width: 0; }
.status-track.p1::after { width: calc(25% - 12.5%); }
.status-track.p2::after { width: calc(50% - 12.5%); }
.status-track.p3::after { width: calc(75% - 12.5%); }
.status-track.p4::after { width: calc(87.5% - 12.5%); }
</style>
